import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useRecordContext, useRefresh } from "react-admin";
import { useNavigate } from "react-router-dom";
import { OneMealService } from "../../services/OneMealService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

const oneMealService = new OneMealService();

const TIME_EXTENSIONS_TAB_INDEX = 5;

interface ExtensionOption {
  extendBy: number;
  label: string;
  price: number;
  newSlotSize: number;
  newEndTime: string;
  isAvailable: boolean;
  unavailableReason: string | null;
}

interface ExtensionOptionsResponse {
  canExtend: boolean;
  reason: string | null;
  currentSlotSize: number;
  currentEndTime: string;
  availableUpTo: number;
  options: ExtensionOption[];
  pendingExtension: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TimeExtensionDialog = ({ open, onClose }: Props) => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [optionsData, setOptionsData] = useState<ExtensionOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<ExtensionOption | null>(null);

  useEffect(() => {
    if (!open || !record?.id) return;
    setLoading(true);
    setSelected(null);
    setOptionsData(null);
    oneMealService
      .getTimeExtensionOptions(Number(record.id))
      .then((data) => { setOptionsData(data); setLoading(false); })
      .catch(() => {
        setLoading(false);
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Failed to load extension options",
          type: CustomSnackbarTypes.ERROR,
        });
      });
  }, [open, record?.id]);

  const handleExtend = async () => {
    if (!selected || !record?.id) return;
    setSubmitting(true);
    try {
      await oneMealService.createTimeExtension(Number(record.id), selected.extendBy);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Time extension created successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });
      refresh();
      onClose();
      navigate(`/OneMealService/${record.id}/show/${TIME_EXTENSIONS_TAB_INDEX}`);
    } catch (err: any) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: err?.response?.data?.error || err?.response?.data?.message || "Failed to create time extension",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Time Extension</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {!loading && optionsData && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Current end time: <strong>{optionsData.currentEndTime}</strong>
              </Typography>
              {optionsData.pendingExtension && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  A pending extension already exists for this visit.
                </Alert>
              )}
              {!optionsData.canExtend && optionsData.reason && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Cannot extend: {optionsData.reason}
                </Alert>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              {optionsData.options.map((opt) => {
                const isSelected = selected?.extendBy === opt.extendBy;
                return (
                  <Box
                    key={opt.extendBy}
                    onClick={() => opt.isAvailable && setSelected(opt)}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      borderRadius: 1,
                      cursor: opt.isAvailable ? "pointer" : "not-allowed",
                      opacity: opt.isAvailable ? 1 : 0.5,
                      backgroundColor: isSelected ? "rgba(25,118,210,0.06)" : "transparent",
                      transition: "border-color 0.15s, background-color 0.15s",
                      "&:hover": opt.isAvailable
                        ? { borderColor: "primary.main", backgroundColor: "action.hover" }
                        : {},
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography fontWeight={600}>{opt.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          New end time: {opt.newEndTime}
                        </Typography>
                        {!opt.isAvailable && opt.unavailableReason && (
                          <Typography variant="caption" color="error">
                            {opt.unavailableReason.replace(/_/g, " ")}
                          </Typography>
                        )}
                      </Box>
                      <Box textAlign="right" display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                        <Typography fontWeight={700} color="primary.main" fontSize={16}>
                          ₹{opt.price}
                        </Typography>
                        {!opt.isAvailable && (
                          <Chip size="small" label="Unavailable" color="error" />
                        )}
                        {opt.isAvailable && isSelected && (
                          <Chip size="small" label="Selected" color="primary" />
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleExtend}
          variant="contained"
          color="primary"
          disabled={!selected || submitting || !!optionsData?.pendingExtension}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Extend"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeExtensionDialog;
